import { createUuid } from './createUuid';
import type { SearchResultHit } from '../dto/searchResultHit';
import type { ExtendedItemDto } from '../dto/extendedItemDto';
import type { DynamoItemType} from '../dto/itemTypes';
import { ItemType } from '../dto/itemTypes';
import type { ItemMetaData, TimedMetadata } from '../dto/fieldValue';
import type { MdfDiff, MdfSchemaNoViews, TimeDurationValueDiff } from '../dto/mdfModels';
import { FieldType } from '../mdf/fieldDefinition';
import type { TimedMetadataCollection,
  TimedMetadataItem, UsageRuleSearchDocument,
  TimedMetadataItemWithUsageRule} from '../dto/timedMetadataModels';
import {
  TIMED_METADATA_FLAVOR,
  isTimedMetadataCollection, isInstanceOfTimedMdfUsageRule,
} from '../dto/timedMetadataModels';
import type {
  TimedMdfUsageRule, UsageRuleDateRange,
  UsageRuleTimingContext} from '../dto/timedMdfUsage';
import { isInstanceOfUsageRuleTimingContext,
} from '../dto/timedMdfUsage';
import { SystemDefaultField } from '../mdf/mdfDefaults';
import { generateDescription, getItemSystemDefaultField, getMdfDiff, getMdfValueForField, isValid, replaceIdWithFieldIdForSchemasAndFillDefaultValues, RestrictedMaterialField } from '../mdf/MetaDataForm';
import { getFieldDefinitionsForMetadata } from '../mdf/subtype';
import { assert } from './invariant';
import type { Logger } from '../types/logger';
import cloneDeep from 'lodash/cloneDeep';


/**
 * Checks if the given timed metadata collection is valid according to the following rules:
 *
 * - all mdf IDs must correspond to models in the given model map
 * - the timespans must be valid (0 <= start < end <= assetDurationMs
 * - the mdf payloads must conform to their models
 *
 * @param data The data to validate
 * @param assetDurationMs The asset duration in milliseconds
 * @param mdfMap A map made of (mdf id,mdf) pairs that lists all the tenant models
 * @return null if the data is valid or an error message otherwise
 */
export const validate = (
  data: TimedMetadataCollection,
  assetDurationMs: number | undefined,
  mdfMap: Record<string, MdfSchemaNoViews>
): string | null => {
  if ((assetDurationMs && assetDurationMs < 0) || !mdfMap) {
    throw new Error('Internal error');
  }

  if (!data || !data.items) {
    return 'Invalid timed metadata payload';
  }

  for (const item of data.items) {
    if (!item.data) {
      return `Timed metadata item missing payload: ${JSON.stringify(item)}`;
    }
    if (!item.data.formId) {
      return `Timed metadata item missing form ID: ${JSON.stringify(item)}`;
    }
    if (!item.data.formData) {
      return `Timed metadata item missing form data: ${JSON.stringify(item)}`;
    }
    const mdf = mdfMap[item.data.formId];
    if (!mdf) {
      return `Timed metadata item referencing unknown MDF ID '${item.data.formId}': ${JSON.stringify(item)}`;
    }
    if (mdf.flavor !== TIMED_METADATA_FLAVOR) {
      return `Timed metadata item referencing non timed metadata model '${item.data.formId}': ${JSON.stringify(item)}`;
    }
    if (!mdf.active) {
      return `Timed metadata item referencing inactive timed metadata model '${item.data.formId}': ${JSON.stringify(item)}`;
    }
    const durationValidation = validateDuration(item, assetDurationMs);
    if (durationValidation) {
      return durationValidation;
    }
    const err = isValid(item.data, mdf);
    if (err) {
      const { fieldMap } = getFieldDefinitionsForMetadata(item.data, Object.values(mdfMap));
      item.data = replaceIdWithFieldIdForSchemasAndFillDefaultValues(item.data, Object.values(fieldMap));
      return `Timed metadata item not conforming its model: ${JSON.stringify(item)}\n\nReason: ${err}`;
    }
  }

  return null;
};

function validateDuration(item: TimedMetadataItem, assetDurationMs: number | undefined): string | null {
  if (assetDurationMs === 0) {
    // If assetDurationMs is 0 we allow having startMs and endMs
    // both be 0 inorder to support adding log events to items without
    // a duration, such as images.
    if (item.startMs === 0 && item.endMs === 0) {
      return null;
    }
  }

  if (item.startMs >= item.endMs || item.startMs < 0) {
    return `Timed metadata item with invalid timespan: ${JSON.stringify(item)}`;
  }
  // If there is a media duration, let's verify that the item does not
  // end past it with a safety margin of 1 second to account for rounding errors
  if (assetDurationMs && item.endMs >= assetDurationMs + 1000) {
    return `Timed metadata item with invalid timespan (should be between 0 and asset duration ${assetDurationMs}): ${JSON.stringify(item)}`;
  }

  return null;
}


/**
 * Given a collection of TimedMetadata, go through each item and generate a default_summary into the formData object if it has
 * valid Text or Choice values.
 *
 * Note: this mutates the object sent in.
 *
 * @param data The data to process
 * @param mdfMap A Map made of (mdf id,mdf) pairs that lists all the tenant models
 */
const generateSummaryForEachItem = (
  logger: Logger,
  data: TimedMetadataCollection,
  mdfMap: Record<string, MdfSchemaNoViews>
) => {
  for (const item of data.items) {
    const summary = generateSummaryForTimedMetadata(logger, mdfMap, item.data);
    if (summary) {
      item.generated_summary = summary;
    }
  }
};

export const generateSummaryForTimedMetadata = (logger: Logger, mdfMap: Record<string, MdfSchemaNoViews>, timedMetadata: TimedMetadata) => {
  const mdf = mdfMap[timedMetadata.formId];

  if (!mdf || !mdf.fields) {
    logger.warn(`No schema found for item formId: ${timedMetadata.formId}`);
    return;
  }

  const validFieldsForSummary: ReadonlySet<string> = new Set(mdf.fields
    .filter(field => field.type === FieldType.Text || field.type === FieldType.Choice)
    .map(field => field.id));

  const idsForSummary = Object.keys(timedMetadata.formData)
    .filter(id => validFieldsForSummary.has(id));

  let summary = '';
  for (const id of idsForSummary) {
    const value = timedMetadata.formData[id];
    if (value) {
      assert(typeof value === 'string', 'Expected value to be a string');
      const field = mdf.fields.find(f => f.id === id);
      if (field?.type === FieldType.Choice && field.alternatives) {
        const alternative = field.alternatives.find(a => a.value === value);
        summary += alternative ? `${alternative.label} ` : `${value} `;
      } else {
        summary += `${value} `;
      }
    }
  }
  if (summary.length > 0) {
    return summary;
  }
}
/**
 * Set a generated description on each of the given metadata items
 * per the rules of item's schema.
 */
const generateDescriptionForEachItem = (data: TimedMetadataCollection, mdfMap: Record<string, MdfSchemaNoViews>) => {
  for (const item of data.items) {
    const description = generateDescriptionFromTimedMetadata(item.data, mdfMap);
    if (description) {
      // The generated description should always follow the schema's current rule, and
      // and so to enforce this we update even if there is a current generated_description
      item.data.generated_description = description;
    }
  }
};

export const generateDescriptionFromTimedMetadata = (timedMetadata: TimedMetadata, mdfMap: Record<string, MdfSchemaNoViews>) => {
  const mdf = mdfMap[timedMetadata.formId];
  if (!mdf) {
    console.warn(`No schema found for item formId: ${timedMetadata.formId}`);
    return;
  }
  return generateDescription(timedMetadata, mdf);
}



export const mapFieldIdToId = (data: TimedMetadataCollection, mdfMap: Record<string, MdfSchemaNoViews>) => {
  for (const item of data.items) {
    const formData = item.data.formData;
    const mdfFields = mdfMap[item.data.formId]?.fields || [];

    for (const [id, value] of Object.entries(formData)) {
      if (value == null) {
        continue; // intentional
      }

      const field = mdfFields.find(f => f.fieldId === id);
      if (!field) {
        continue;
      }

      delete formData[field.fieldId];
      formData[field.id] = value;
    }
  }
};

export const getIngestTimeFromItem = (item: Pick<ExtendedItemDto, 'metadata' | 'createdOn'>) => {
  return getItemSystemDefaultField({ metadata: item.metadata }, SystemDefaultField.CreatedOn) ?? item.createdOn;
};

export const applyIngestTimeIfUsageRuleExists = (timedMetadata: TimedMetadataItem, createdOnDate: string | undefined) => {
  const timedFormData = timedMetadata.data;
  if (timedFormData.formData[RestrictedMaterialField.UsageRule] && !timedFormData.formData[RestrictedMaterialField.IngestTime]) {
    timedFormData.formData[RestrictedMaterialField.IngestTime] = createdOnDate ?? new Date().toISOString();
  }
};

export type CalculatedUsageContext = UsageRuleTimingContext & {
  validFrom: string | 'forever';
  validUntil: string | 'forever';
  hasReachedStartDate: boolean;
  isExpired: boolean;
};

export type UsageRuleWithCalculatedValidity = Omit<TimedMdfUsageRule, 'contexts'> & {
  contexts: CalculatedUsageContext[];
  hasDisallowedContext: boolean;
};

/**
 * Returns contexts with calculated date value according to current date for the time ranges
 */
const getCalculatedUsageContext = (contexts: UsageRuleTimingContext[], ingestTime: string) => {
  return (contexts)
    .filter(isInstanceOfUsageRuleTimingContext)
    .map(ctx => ({
      ...ctx,
      validFrom: calculateDateFromUsageRuleRange(ctx.validity.from, ingestTime),
      validUntil: calculateDateFromUsageRuleRange(ctx.validity.to, ingestTime)
    }));
};

export const getUsageRuleInfoFromTimedMdf = (timedMetadataWithUsageRule: TimedMetadataItemWithUsageRule): UsageRuleWithCalculatedValidity | undefined => {
  const ingestTime = timedMetadataWithUsageRule.data.formData[RestrictedMaterialField.IngestTime] as string;
  const usageRule = timedMetadataWithUsageRule.usageRule;
  if (ingestTime && usageRule) {
    return getCalculatedUsageRule(usageRule, ingestTime);
  }
};

/**
 * Returns a usage rule with calculated dates for each context and a property defining whether
 * this rule has any disallowed context.
 */
export const getCalculatedUsageRule = (usageRule: TimedMdfUsageRule, ingestTime: string): UsageRuleWithCalculatedValidity | undefined => {
  let hasDisallowedContext = false;
  const calculatedContexts = getCalculatedUsageContext(usageRule.contexts, ingestTime).map(ctx => {
    const validFrom = ctx.validFrom ?? 'forever';
    const validUntil = ctx.validUntil ?? 'forever';
    const hasReachedStartDate = isCurrentDateWithinRange(validFrom, 'start');
    const isExpired = !isCurrentDateWithinRange(validUntil, 'end');
    if (!hasDisallowedContext && (!hasReachedStartDate || isExpired)) {
      hasDisallowedContext = true;
    }
    return {
      ...ctx,
      validFrom,
      validUntil,
      hasReachedStartDate,
      isExpired
    };
  });
  return {
    ...usageRule,
    contexts: calculatedContexts,
    hasDisallowedContext
  };
};

/**
 * Returns true if the date string provided is within the current date, false otherwise
 */
const isCurrentDateWithinRange = (dateString: string | 'forever', type: 'start' | 'end') => {
  if (dateString === 'forever') {
    return true;
  }
  const currentDateObject = new Date();
  const dateObject = new Date(dateString);
  const isSameDay = currentDateObject.toDateString() === dateObject.toDateString();
  if (isSameDay) {
    return true;
  }

  if (type === 'start') {
    return currentDateObject >= dateObject;
  }
  if (type === 'end') {
    return currentDateObject <= dateObject;
  }
  return false;
};

/**
 * Returns ISO date string if the range is offset from ingest time or on a specific date
 * Returns undefined if range is 'forever'
 */
const calculateDateFromUsageRuleRange = (range: UsageRuleDateRange, ingestTime: string): string | undefined => {
  switch (range.offsetFrom) {
    case 'ingest_time':
      const ingestDate = new Date(ingestTime);
      if (range.untilNumberOfDays > 0) {
        ingestDate.setDate(ingestDate.getDate() + range.untilNumberOfDays);
      }
      return ingestDate.toISOString();
    case 'absolute':
      return range.absoluteDate;
    case 'forever':
      return undefined;
  }
};


/**
 * Transforms usage rules to docs according to the search index definition.
 * Dates are calculated from the ranges. Strips values that are not in the definition.
 */
export const getUsageRuleSearchDocsFromTimedMetadata = (timedMdfs: TimedMetadataItemWithUsageRule[], itemCreationDate: string): UsageRuleSearchDocument[] => {
  const docs: UsageRuleSearchDocument[] = [];
  for (const tmdf of timedMdfs) {
    if (isInstanceOfTimedMdfUsageRule(tmdf.usageRule)) {
      const { identifier, contexts } = tmdf.usageRule;
      const ingestTime = (tmdf.data.formData[RestrictedMaterialField.IngestTime] as string | undefined) || itemCreationDate;
      docs.push({
        ruleKey: identifier.key,
        startMs: tmdf.startMs,
        endMs: tmdf.endMs,
        ingestTime,
        contexts: getCalculatedUsageContext(contexts, ingestTime)
          .map(({ name, validFrom, validUntil, warningLevel }) => ({ name, validFrom, validUntil, warningLevel }))
      });
    }
  }
  return docs;
};

export const getTimedMdfDiff = (
  newTimedMetaData: TimedMetadataItem | null,
  oldTimedMetaData: TimedMetadataItem | undefined
): MdfDiff => {
  const startTimeDiff: TimeDurationValueDiff | undefined = (oldTimedMetaData?.startMs !== newTimedMetaData?.startMs) ? { oldValue: oldTimedMetaData?.startMs, newValue: newTimedMetaData?.startMs } : undefined;

  const durationDiff: TimeDurationValueDiff | undefined = (oldTimedMetaData?.endMs !== newTimedMetaData?.endMs) ? {oldValue: oldTimedMetaData?.endMs, newValue: newTimedMetaData?.endMs} : undefined;

  const timedMetaDataDiff: MdfDiff = getMdfDiff(oldTimedMetaData?.data, newTimedMetaData?.data);

  if (startTimeDiff) {
    timedMetaDataDiff.startTime = startTimeDiff;
  }
  if (durationDiff) {
    timedMetaDataDiff.duration = durationDiff;
  }
  return timedMetaDataDiff;
};

/**
 * Creates a TimedMetadataItem object item for given ItemMetaData
 * @param indefiniteEndPoint Whether this timed metadata is initialized with an unknown end point
 * @returns created TimedMetadataItem object
 */
export const createTimedMetadataItem = (metadata: ItemMetaData, startMs: number, endMs: number, itemType: DynamoItemType | undefined, source: 'sidecar' | 'api', indefiniteEndPoint = false) => {
  const timedMdf: TimedMetadataItem = {
    startMs,
    endMs: endMs,
    id: createUuid(),
    data: metadata,
    initializedWithIndefiniteEndMs: indefiniteEndPoint,
    creationInfo: {
      date: new Date().toISOString(),
      source,
      userId: null,
    }
  };
  normaliseIngestedTimedMdfEvent(timedMdf, itemType);
  applyIngestTimeIfUsageRuleExists(timedMdf, new Date().toISOString());
  return timedMdf;
};

export const addSummaryAndDescriptionToTimedMetadata = (
  logger: Logger,
  timedMdfCollection: TimedMetadataCollection,
  mdfMap: Record<string, MdfSchemaNoViews>
) => {
  generateSummaryForEachItem(logger, timedMdfCollection, mdfMap);
  generateDescriptionForEachItem(timedMdfCollection, mdfMap);

  return timedMdfCollection;
};

function normaliseIngestedTimedMdfEvent(event: TimedMetadataItem, itemType: DynamoItemType | undefined) {
  if (itemType === ItemType.IMAGE) {
    // Only 0 is valid for start and end.
    event.startMs = 0;
    event.endMs = 0;
    // This prop isn't applicable for images as they don't have any duration
    delete event.initializedWithIndefiniteEndMs;
    return;
  }

  // We don't know the item duration at this point, but to avoid having
  // to throw away the log event due to invalid values we try to do some
  // simple corrections if the event duration values are invalid.
  if ((typeof event.startMs !== 'number') || event.startMs < 0) {
    event.startMs = 0;
  }
  if ((typeof event.endMs !== 'number') || event.endMs <= event.startMs) {
    event.endMs = event.startMs + 500;
  }
}

/**
 * Replaces "endMs" value of timed metadata items with initializedWithIndefiniteEndMs = true
 * and if replaced, sets initializedWithIndefiniteEndMs = false.
 * @returns boolean true/false indicating whether any value was replaces
 */
export const replaceIndefiniteEndMsIfAny = (timedMdfCollection: TimedMetadataCollection, mediaDuration: number) => {
  let hasAny = false;
  for (const tm of timedMdfCollection.items) {
    if (tm.initializedWithIndefiniteEndMs) {
      hasAny = true;
      tm.endMs = mediaDuration;
      tm.initializedWithIndefiniteEndMs = false;
    }
  }
  return hasAny;
};

export const createDefaultIngestTimedMetadataCollectionWithoutValidation = (defaultTimedMetadata: ItemMetaData, itemType: DynamoItemType): TimedMetadataCollection => {
  const defaultTimedMetadataCollection = {
    items: [createTimedMetadataItem(defaultTimedMetadata, 0, 1, itemType, 'sidecar', true)]
  };
  return defaultTimedMetadataCollection;
};

export const createDefaultIngestTimedMetadataCollection = (
  logger: Logger,
  defaultTimedMetadata: ItemMetaData,
  itemType: DynamoItemType,
  mdfMap: Record<string, MdfSchemaNoViews>
): TimedMetadataCollection | undefined => {
  const defaultTimedMetadataCollection = createDefaultIngestTimedMetadataCollectionWithoutValidation(defaultTimedMetadata, itemType);

  // Some items (e.g. images) are initialized with 0 as the value for start and end point.
  // So, the assetDuration also needs to be 0 for them to pass validation.
  const createdDefaultTimedMetadata = defaultTimedMetadataCollection.items[0];
  const assetDuration = createdDefaultTimedMetadata?.startMs === 0 && createdDefaultTimedMetadata.endMs === 0 ? 0 : undefined;

  const possibleError = validate(defaultTimedMetadataCollection, assetDuration, mdfMap);
  if (possibleError) {
    logger.error(possibleError);
    return undefined;
  }
  return defaultTimedMetadataCollection;
};

export const getMergedTimedMetadataCollection = (collections: (Partial<TimedMetadataCollection> | undefined)[]) => {
  const mergedCollection: TimedMetadataCollection = { items: [] };
  for (const item of (collections ?? [])) {
    if (isTimedMetadataCollection(item)) {
      mergedCollection.items.push(...item.items);
    }
  }
  return mergedCollection.items.length > 0 ? mergedCollection : undefined;
};

export const doesSearchHitHaveRestrictedEvents = (item: Pick<SearchResultHit, 'id' | 'usageRules' | 'hasRestrictedMaterial'>) => {
  if ((item.usageRules?.length ?? 0) > 0 && item.usageRules?.every(r => r.contexts)) {
    const expiredRule = item.usageRules.find(rule => {
      const restrictedContext = !!rule.contexts.find(context => {
        if (context.warningLevel === 'never') {
          return false;
        }
        if (context.warningLevel === 'always') {
          return true;
        }
        const validFrom = context.validFrom ?? 'forever';
        const validUntil = context.validUntil ?? 'forever';
        const hasReachedStartDate = validFrom === 'forever' || isCurrentDateWithinRange(validFrom, 'start');
        const hasExpired = validUntil !== 'forever' && !isCurrentDateWithinRange(validUntil, 'end');
        return !hasReachedStartDate || hasExpired;
      });
      return restrictedContext;
    });
    return !!expiredRule;
  }
  return !!item.hasRestrictedMaterial;
};


const hasValue = (key: string, obj: Record<string, unknown>) => obj[key] != undefined && (typeof obj[key] !== 'string' || obj[key].length > 0);

export const getTimedMetadataWithDefaultValues = (timedMetadataItem: TimedMetadataItem, mdfMap: Record<string, MdfSchemaNoViews>) => {
  const { fieldMap } = getFieldDefinitionsForMetadata(timedMetadataItem.data, Object.values(mdfMap));
  const clone = cloneDeep(timedMetadataItem.data);
  for (const field of Object.values(fieldMap)) {
    const formDataKey = hasValue(field.id, clone.formData) ? field.id : field.fieldId;
    const valueToAssign = getMdfValueForField(clone.formData[formDataKey], field, true);
    if (valueToAssign !== undefined) {
      clone.formData[formDataKey] = valueToAssign;
    }
  }
  return clone;
};

